import { useSuspenseQuery } from '@tanstack/react-query';
import { createFileRoute } from '@tanstack/react-router';
import defaults from 'lodash-es/defaults';
import { useMemo } from 'react';

import { useTranslation } from '@apple/lib/i18next';
import { BlockNavigation } from '@apple/ui/form/components/BlockNavigation';
import { ServerValidatedZodForm } from '@apple/ui/form/components/ValidatedForm/ServerValidatedZodForm';
import { StepLayout } from '@apple/ui/layouts/step';
import { useLogger } from '@apple/utils/logging/useLogger';

import { StoreEventDetails } from '@apple/features/store-events/components/details';
import { ShipmentReportLink } from '@apple/features/store-events/components/shipment-report-link';
import { useArchiveStoreEvent } from '@apple/features/store-events/hooks/use-archive-store-event';
import { useEditStoreEvent } from '@apple/features/store-events/hooks/use-edit-store-event';
import {
	DEFAULT_STORE_EVENT_UPDATE_MODEL,
	type StoreEventUpdateModel,
	storeEventUpdateModelSchema,
} from '@apple/features/store-events/models/management';
import { storeEventQueries } from '@apple/features/store-events/queries';

export const Route = createFileRoute('/_authed/store-events/_steps/$storeEventId/details')({
	component: RouteComponent,
	wrapInSuspense: true,
});

function RouteComponent() {
	const { t } = useTranslation('store-event');
	const navigate = Route.useNavigate();
	const { storeEventId } = Route.useParams();
	const editMutation = useEditStoreEvent(storeEventId);
	const archiveMutation = useArchiveStoreEvent(storeEventId);
	const storeEventQuery = useSuspenseQuery(storeEventQueries.getById(storeEventId));

	// TODO: Make this into a custom hook (maybe useFormInitialValues(defaultValues, currentValues) or something)
	const initialValues = useMemo<StoreEventUpdateModel>(() => {
		return defaults(storeEventQuery.data, DEFAULT_STORE_EVENT_UPDATE_MODEL);
	}, [storeEventQuery.data]);
	const canUpdateCategories =
		storeEventQuery.data.canBeCompleted && !storeEventQuery.data.canBeSubmitted;
	useLogger({
		log: 'store-events',
		name: 'StoreEventDetails',
		props: [{ storeEvent: storeEventQuery.data, initialValues }],
	});

	return (
		<ServerValidatedZodForm
			name={'store-event-details'}
			validationType='EditEventDetails'
			schema={storeEventUpdateModelSchema}
			initialValues={initialValues}
			ignoreModelErrorParent
			onValidSubmit={storeEvent =>
				editMutation.mutateAsync(storeEvent, {
					onSuccess: async () => {
						await navigate({
							to: canUpdateCategories
								? '/store-events/$storeEventId/categories'
								: '/store-events/$storeEventId/products',
							params: {
								storeEventId,
							},
						});
					},
				})
			}
		>
			{({ form, isSubmitting }) => (
				<StepLayout
					queryErrors={[storeEventQuery.error]}
					mutationErrors={[editMutation.error, archiveMutation.error]}
					buttons={[
						{
							text: t('common:buttons.back'),
							to: '/store-events',
						},
						{
							text: t('labels.archive'),
							hidden: !storeEventQuery.data.canBeArchived,
							onClick: () =>
								archiveMutation.mutate(undefined, {
									onSuccess: () => {
										navigate({
											to: '/store-events',
										});
									},
								}),
							color: 'red',
							variant: 'filled',
							disabled:
								form.isDirty() ||
								editMutation.isPending ||
								archiveMutation.isPending ||
								isSubmitting,
							loading: archiveMutation.isPending,
						},
						{
							text: t('common:pagination.nextShort'),
							type: 'submit',
							color: 'blue',
							variant: 'filled',
							disabled:
								editMutation.isPending || archiveMutation.isPending || isSubmitting,
							loading: editMutation.isPending || isSubmitting,
						},
					]}
				>
					<BlockNavigation
						condition={
							// Stop navigation when the form is dirty and not saved
							form.isDirty() &&
							!(editMutation.isPending || editMutation.isSuccess || isSubmitting)
						}
					/>
					<StoreEventDetails
						id={storeEventQuery.data.id}
						hasShipmentReport={storeEventQuery.data.hasErpDetails}
						form={form}
						storeId={storeEventQuery.data.storeNumber ?? undefined}
						storeUtcOffsetMinutes={storeEventQuery.data.storeUtcOffsetMinutes}
						renderShipmentReportLink={ShipmentReportLink}
					/>
				</StepLayout>
			)}
		</ServerValidatedZodForm>
	);
}
